import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const prettify = (value) => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/** Apply one status to every selected record. */
export function BulkStatusDialog({
  open, onOpenChange, moduleLabel, count, statuses = [], reasonRequired = [], loading, onConfirm,
}) {
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const needsReason = reasonRequired.includes(status);
  const ready = status && (!needsReason || reason.trim()) && !loading;

  const close = (value) => {
    if (!value) { setStatus(''); setReason(''); }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="bulk-status-dialog">
        <DialogHeader>
          <DialogTitle>Change status of {count} {moduleLabel}</DialogTitle>
          <DialogDescription>The same status is applied to every selected record.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>New status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="bulk-status-select">
                <SelectValue placeholder="Pick a status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((value) => (
                  <SelectItem key={value} value={value} data-testid={`bulk-status-option-${value}`}>
                    {prettify(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {needsReason && (
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why are these being closed?"
                data-testid="bulk-status-reason-input"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="bulk-status-cancel">Cancel</Button>
          <Button disabled={!ready} onClick={() => onConfirm(status, reason.trim())} data-testid="bulk-status-confirm">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Apply to {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkStatusDialog;
